<template>
  <div>
    <!--    upper field label-->
    <div
      v-if="upperLabel"
      class="select-field-label"
      :title="upperLabel"
    >
      {{ upperLabel }}
    </div>

    <v-select
      ref="select"
      v-model="selectValue"
      :append-icon="appendIcon"
      :class="getFieldClasses()"
      hide-details="auto"
      :cache-items="cacheItems"
      item-text="displayText"
      :item-value="getItemValue()"
      :autofocus="autofocus"
      :chips="chips"
      :color="color"
      :counter="counter"
      :dark="dark"
      :items="processedItems"
      :label="label"
      :loading="loading"
      :menu-props="menuProps"
      :multiple="multiple"
      :messages="messages"
      :open-on-clear="openOnClear"
      :outlined="outlined"
      :placeholder="placeholder"
      :small-chips="smallChips"
      :readonly="readonly"
      :return-object="returnObject"
      :prefix="prefix"
      :suffix="suffix"
      :disabled="disabled"
      :validate-on-blur="validateOnBlur"
      :no-data-text="noDataText"
      :height="height"
      :full-width="fullWidth"
      :dense="dense"
      :filled="filled"
      :clearable="clearable"
      :rules="rules"
      :error-messages="getValidationErrors()"
      :error-count="errorCount"
      @change="onChange"
    >
      <!-- A multiple select is ticked off rather than picked from, so its items
           carry the same checkbox every other list in the app does instead of
           Vuetify's own - see `ImageCheckbox`. -->
      <template
        v-if="multiple"
        #item="{ item, attrs, on }"
      >
        <v-list-item
          v-bind="attrs"
          class="select-field-item"
          :class="{ 'select-field-item--selected': attrs.inputValue }"
          v-on="on"
        >
          <v-list-item-action class="select-field-item__action">
            <!-- Only ever shows the state; the row's own click is what toggles
                 it, so the checkbox is left as a picture of it. -->
            <image-checkbox
              :value="!!attrs.inputValue"
              :disabled="disabled"
            />
          </v-list-item-action>

          <v-list-item-content>
            <v-list-item-title class="select-field-item__title">
              {{ item.displayText }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-select>
  </div>
</template>

<script>
import { isEmpty } from 'lodash'
import ImageCheckbox from '@/global/components/fields/ImageCheckbox.vue'

const DEFAULT_ITEM_VALUE = 'id'
const DEFAULT_ITEM_TEXT = 'name'
export default {
  name: 'SelectField',

  components: {
    ImageCheckbox
  },

  props: {
    value: {
      type: null, // accepts any type
      default: null // default value
    },
    config: {
      type: Object,
      default: () => ({})
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    // Only the edit-mode look is read off these - the field derives whether it
    // can actually be changed from its own config (see `disabled`).
    editMode: {
      type: Boolean,
      default: false
    },
    createMode: {
      type: Boolean,
      default: false
    }
  },

  emits: [
    'input'
  ],

  data () {
    return {
      internalItems: []
    }
  },

  computed: {
    selectValue: {
      get () {
        return this.value
      },
      set (newValue) {
        this.$emit('input', newValue)
      }
    },

    /**
     * Defines field key.
     *
     * @default null
     * @type {string}
     */
    fieldKey () {
      return this.config?.key || null
    },

    /**
     * Define label that will be shown above the field.
     *
     * @default ''
     * @type {string}
     */
    upperLabel () {
      return this.config?.upperLabel || ''
    },

    /**
     * Appends an icon to the component, uses the same syntax as v-icon.
     *
     * @default '$dropdown'
     * @type {string}
     */
    appendIcon () {
      return this.config?.appendIcon || '$dropdown'
    },

    /**
     * Enables autofocus.
     *
     * @default false
     * @type {boolean}
     */
    autofocus () {
      return this.getBooleanConfigProperty('autofocus')
    },

    /**
     * Keeps a local unique copy of all items that have been passed through the items prop.
     *
     * @default false
     * @type {boolean}
     */
    cacheItems () {
      return this.getBooleanConfigProperty('cacheItems')
    },

    /**
     * Changes display of selections to chips.
     *
     * @default false
     * @type {boolean}
     */
    chips () {
      return this.getBooleanConfigProperty('chips')
    },

    /**
     * Applies specified color to the control - it can be the name of material color (for example success or purple) or CSS color (#033 or rgba(255, 0, 0, 0.5)). You can find a list of built-in classes on the colors page.
     *
     * @default undefined
     * @type {string}
     */
    color () {
      return this.config?.color
    },

    /**
     * Creates counter for input length; if no number is specified, it defaults to 25. Does not apply any validation.
     *
     * @default undefined
     * @type {boolean | number | string}
     */
    counter () {
      return this.config?.counter
    },

    /**
     * Changes display of selections to chips.
     *
     * @default false
     * @type {boolean}
     */
    dark () {
      return this.getBooleanConfigProperty('dark')
    },

    /**
     * Can be an array of objects or array of strings. When using objects, will look for a text, value and disabled keys. This can be changed using the item-text, item-value and item-disabled props. Objects that have a header or divider property are considered special cases and generate a list header or divider; these items are not selectable.
     *
     * @default []
     * @type {array}
     */
    items () {
      return this.config?.items || []
    },

    /**
     * Sets input label.
     *
     * @default undefined
     * @type {string}
     */
    label () {
      return this.config?.label
    },

    /**
     * Displays linear progress bar. Can either be a String which specifies which color is applied to the progress bar (any material color or theme color - **primary**, **secondary**, **success**, **info**, **warning**, **error**) or a Boolean which uses the component color (set by color prop - if it’s supported by the component) or the primary color.
     *
     * @default false
     * @type {boolean | string}
     */
    loading () {
      return this.config?.loading !== undefined ? this.config.loading : false
    },

    /**
     * Pass props through to the v-menu component. Accepts either a string for boolean props menu-props="auto, overflowY", or an object :menu-props="{ auto: true, overflowY: true }".
     *
     * @default false
     * @type {string | array | object}
     */
    menuProps () {
      return this.config?.menuProps || {
        closeOnClick: false,
        closeOnContentClick: false,
        disableKeys: true,
        openOnClick: false,
        maxHeight: 304,
        bottom: true,
        offsetY: true
      }
    },

    /**
     * Changes select to multiple. Accepts array for value.
     *
     * @default false
     * @type {boolean}
     */
    multiple () {
      return this.getBooleanConfigProperty('multiple')
    },

    /**
     * Displays a list of messages or message if using a string.
     *
     * @default []
     * @type {string | array}
     */
    messages () {
      return this.config?.messages || []
    },

    /**
     * When using the clearable prop, once cleared, the select menu will either open or stay open, depending on the current state.
     *
     * @default false
     * @type {boolean}
     */
    openOnClear () {
      return this.getBooleanConfigProperty('openOnClear')
    },

    /**
     * Applies the outlined style to the input.
     *
     * @default true
     * @type {boolean}
     */
    outlined () {
      return this.getBooleanConfigProperty('outlined', true)
    },

    /**
     * Sets the input’s placeholder text.
     *
     * @default undefined
     * @type {string}
     */
    placeholder () {
      return this.config?.placeholder
    },

    /**
     * Changes display of selections to chips with the small property.
     *
     * @default false
     * @type {boolean}
     */
    smallChips () {
      return this.getBooleanConfigProperty('smallChips')
    },

    /**
     * Puts input in readonly state.
     *
     * @default false
     * @type {boolean}
     */
    readonly () {
      return this.getBooleanConfigProperty('readonly')
    },

    /**
     * Changes the selection behavior to return the object directly rather than the value specified with item-value.
     *
     * @default false
     * @type {boolean}
     */
    returnObject () {
      return this.getBooleanConfigProperty('returnObject')
    },

    /**
     * Displays prefix text.
     *
     * @default undefined
     * @type {string}
     */
    prefix () {
      return this.config?.prefix
    },

    /**
     * Displays suffix text.
     *
     * @default undefined
     * @type {string}
     */
    suffix () {
      return this.config?.suffix
    },

    /**
     * Delays validation until blur event.
     *
     * @default false
     * @type {boolean}
     */
    validateOnBlur () {
      return this.getBooleanConfigProperty('validateOnBlur')
    },

    /**
     * Display text when there is no data.
     *
     * @default this.$t('base.no_data')
     * @type {string}
     */
    noDataText () {
      return this.config?.noDataText || this.$t('base.no_data')
    },

    /**
     * Sets the height of the input.
     *
     * @default undefined
     * @type {number | string}
     */
    height () {
      return this.config?.height
    },

    /**
     * Designates input type as full-width.
     *
     * @default false
     * @type {boolean}
     */
    fullWidth () {
      return this.getBooleanConfigProperty('fullWidth')
    },

    /**
     * Applies the alternate filled input style.
     *
     * @default false
     * @type {boolean}
     */
    filled () {
      return this.getBooleanConfigProperty('filled')
    },

    /**
     * The total number of errors that should display at once.
     *
     * @default 0
     * @type {number}
     */
    errorCount () {
      return this.config.errorCount ?? this.getValidationErrors()?.length ?? 0
    },

    /**
     * Disables the input.
     *
     * @default false
     * @type {boolean}
     */
    disabled () {
      return this.getBooleanConfigProperty('disabled')
    },

    // Clearable unless the config says otherwise. Vuetify draws the clear icon
    // on readonly inputs too (and it stays clickable there), so the field also
    // has to actually be editable for it to make sense.
    clearable () {
      return this.getBooleanConfigProperty('clearable', true) && !this.readonly && !this.disabled
    },

    rules () {
      return this.config?.rules || []
    },

    // The green fill every other field wears while its tab is being edited -
    // see TextField/AutocompleteField. A field that cannot be changed keeps the
    // white box: the state is carried by the box only where it means something.
    isEditingStyled () {
      const { useDefaultEditStyle = true } = this.config || {}

      return useDefaultEditStyle && this.editMode && !this.disabled && !this.readonly
    },

    /**
     * Reduce the input height.
     *
     * @default true
     * @type {boolean}
     */
    dense () {
      return this.getBooleanConfigProperty('dense', true)
    },

    /**
     * Set property of ***item’s*** text value.
     *
     * @default text
     * @type {string | array | function}
     */
    itemText () {
      return this.config?.itemText || DEFAULT_ITEM_TEXT
    },

    /**
     * Set property of ***item’s*** value - ***must be primitive***. Dot notation is supported. ***Note***: This is currently not supported with v-combobox
     *
     * @default value
     * @type {string | array | function}
     */
    itemValue () {
      return this.config?.itemValue || 'value'
    },

    /**
     * Items that we convert to configurable custom shape.
     *
     * @default []
     * @type {array}
     */
    processedItems () {
      const mappedItems = this.internalItems.map(item => ({
        ...item,
        displayText: this.getItemText(item)
      }))
      return mappedItems?.filter(item => !!item?.displayText) || []
    },

    /**
     * Define callback for custom item text for items.
     *
     * @default null
     * @type {function}
     */
    customItemText () {
      return this.config?.customItemText || null
    },

    /**
     * Define callback for custom item value for items.
     *
     * @default null
     * @type {string | object}
     */
    customItemValue () {
      return this.config?.customItemValue || null
    }
  },

  watch: {
    items: {
      immediate: true,
      deep: true,
      async handler (items) {
        if (items?.length) {
          this.internalItems = items.map(item => this.normalizeId(item))
        }
      }
    }
  },

  methods: {
    /**
     * Get item text value for items.
     * Define callback 'customItemText' or string value that should represent item text.
     *
     * @param {object} item - single item
     * @returns {string | object}
     */
    getItemText (item) {
      if (typeof this.customItemText === 'function') {
        const result = this.customItemText(item)
        return result && typeof result === 'string' ? result : null
      }
      const text = item?.[this.itemText]
      return text && typeof text === 'string' ? text : null
    },

    /**
     * Get item value for items.
     * Define 'customItemValue' that should represent item value.
     *
     * @returns {string}
     */
    getItemValue () {
      return this.customItemValue && typeof this.customItemValue === 'string' ? this.customItemValue : DEFAULT_ITEM_VALUE
    },

    /**
     * If no custom item value provided, default is item's 'id' value.
     * Normalize id to be always type of number, because sometimes it can come as a string.
     *
     * @returns {object}
     */
    normalizeId (item) {
      if (typeof item !== 'object' || item === null) {
        return { id: item, name: String(item) }
      }

      const id = item.id

      if (typeof id === 'string') {
        // If a string contains any letters, keep it as-is
        if (/[a-zA-Z]/.test(id)) {
          return { ...item, id }
        }
        else {
          const parsed = parseInt(id, 10)
          return { ...item, id: isNaN(parsed) ? undefined : parsed }
        }
      }
      else if (typeof id === 'number') {
        return { ...item, id }
      }
      else {
        return { ...item, id }
      }
    },

    /**
     * Get boolean property from config.
     *
     * @param {string} key - property name
     * @param {boolean} [defaultValue=false] - false
     * @returns {boolean}
     */
    getBooleanConfigProperty (key, defaultValue = false) {
      return this.config?.[key] !== undefined
        ? !!this.config[key]
        : defaultValue
    },

    /**
     * Get classes for field.
     * Define custom classes via config, under 'class' property.
     *
     * @returns {string}
     */
    getFieldClasses () {
      const fieldClass = this.config?.class

      return [
        'mt-1',
        this.isEditingStyled && 'select-field--is-editing',
        this.getValidationErrors().length && 'select-field--has-error',
        typeof fieldClass === 'string' && fieldClass
      ].filter(Boolean).join(' ')
    },

    getValidationErrors () {
      let messages = []
      const { validationPropertyKey } = this.config || {}

      if (isEmpty(this.validationErrors) || !this.fieldKey || this.disabled) {
        return messages
      }

      for (const errorKey in this.validationErrors) {
        if (errorKey === this.fieldKey && !this.collectionKey) {
          messages = this.validationErrors[errorKey]
        }
        else if (validationPropertyKey && errorKey === validationPropertyKey) {
          messages = this.validationErrors[errorKey]
        }
      }

      return messages
    },

    onChange (index) {
      // bugfix for v-select field
      // do not scroll to the top of dropdown menu on selection
      this.$refs?.select?.setMenuIndex(index)
    }
  }
}
</script>

<style scoped lang="scss">
/* No grey background for disabled fields (see TextField) - the state is carried
   by the field's text and cursor, not by the box. */

.select-field-label {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  line-height: 20px;
  font-weight: 500;
  /* Never wrap - see TextField. The full text stays readable through the
     element's `title` on hover. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep {
  .v-text-field--outlined fieldset {
    border-color: #E5E7EB !important;
    border-radius: 8px !important;
  }

  .v-input > .v-input__control > .v-input__slot > .v-select__slot > .v-select__selections {
    font-family: 'Satoshi', sans-serif;
    font-size: 14px !important;
    line-height: 20px !important;
    color: #111827;
    font-weight: 500 !important;
  }

  .select-field--is-editing fieldset {
    border-color: #65AD45 !important;
  }

  // Scoped to the input box itself (`.v-input__slot`) rather than the whole
  // `.v-input`, which also holds the validation message row below it - see
  // AutocompleteField.
  .select-field--is-editing > .v-input__control > .v-input__slot {
    background-color: #F7FEE7 !important;
  }

  // Placed after the default/editing border rules above so it wins the
  // (equal-specificity) cascade regardless of edit state.
  .select-field--has-error fieldset {
    border-color: #DC2626 !important;
  }
}

</style>

<!-- The dropdown is rendered in a menu detached from this component's root, so
     the scoped block above cannot reach its items - hence the plain block below,
     narrowed by the `select-field-item` class the item slot puts on each row. -->
<style lang="scss">
.select-field-item {
  .select-field-item__action {
    margin-right: 12px;
  }

  .select-field-item__title {
    font-family: 'Satoshi', sans-serif;
    font-size: 14px !important;
    line-height: 20px !important;
    font-weight: 500 !important;
    color: #111827 !important;
  }

  // A ticked item is marked in the checkbox's own green rather than in
  // Vuetify's default primary tint, and keeps its text colour - the tick and
  // the row now say the same thing.
  &.select-field-item--selected {
    background-color: rgba(101, 173, 69, 0.12) !important;

    // Vuetify paints an active item through this overlay in the primary
    // colour; the background above is what marks the row now.
    &::before {
      opacity: 0 !important;
    }
  }
}
</style>
